<template>
    <div class="time"
         :class="{ 'time--animated': animate, 'time--danger': remainingTime <= 30 }">
        {{ formatSeconds(remainingTime) }}
    </div>
</template>

<script setup lang="ts">
    import { onBeforeUnmount, ref, watch } from 'vue';
    import { useUserStore } from '@/js/store/quests/dashboard/UserStore';

    const userStore = useUserStore();

    const animate = ref(false);
    const remainingTime = ref(userStore.activeUserRoundData?.remaining_time);
    const timerId = ref<number | null>(null);

    const formatSeconds = (time: number) => {
        const hours = Math.floor(time / 3600);
        const minutes = Math.floor((time - (hours * 3600)) / 60);
        const seconds = time - (hours * 3600) - (minutes * 60);

        return [hours, minutes, seconds].map((v) => `0${v}`.slice(-2)).join(':');
    };

    const startCountdown = () => {
        if (timerId.value !== null) {
            cancelAnimationFrame(timerId.value);
        }

        const startTime = Date.now();
        const endTimestamp = startTime + remainingTime.value * 1000;

        const updateCountdown = () => {
            const now = Date.now();
            const remainingTimeMs = endTimestamp - now;

            if (remainingTimeMs > 0) {
                remainingTime.value = Math.floor(remainingTimeMs / 1000);
                if (remainingTime.value <= 30) {
                    animate.value = true;
                }
                timerId.value = requestAnimationFrame(updateCountdown);
            } else {
                cancelAnimationFrame(timerId.value);
                remainingTime.value = 0;
                animate.value = false;

                if (userStore.activeUserRoundData) {
                    try {
                        userStore.cancelRoundByTimeEnd();
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error('Failed to cancel round', error);
                    }
                }

                try {
                    userStore.fetchActiveUserRoundData();
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to fetch active user round data', error);
                }
            }
        };

        timerId.value = requestAnimationFrame(updateCountdown);
    };

    watch(
        () => userStore.activeUserRoundData,
        (newRoundData) => {
            if (newRoundData?.started_at) {
                remainingTime.value = newRoundData?.remaining_time || 0;
                startCountdown();
            }
        },
        { immediate: true },
    );

    onBeforeUnmount(() => {
        if (timerId.value !== null) {
            cancelAnimationFrame(timerId.value);
        }
    });
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;
@use "@/sass/design-system/fonts" as fonts;
@use "@/sass/partials/breakpoints" as breakpoints;

.time {
    color: variables.$baseWhite;
    font-family: fonts.$font-quicksand;
    font-size: 40px;
    font-weight: 700;
    line-height: 1;

    &--danger {
        color: variables.$error500;
    }

    &--animated {
        animation: shake 1s linear;
        animation-iteration-count: infinite;
    }

    @media (max-width: breakpoints.$lg-breakpoint-max-width) {
        font-size: 24px;
    }

    @media (max-width: breakpoints.$md-breakpoint-max-width) {
        font-size: 13px;
    }
}

@keyframes shake {
    0% {
        transform: translateX(0);
    }
    7% {
        transform: translateX(10px);
    }
    15% {
        transform: translateX(-10px);
    }
    23% {
        transform: translateX(10px);
    }
    30% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(0);
    }
}
</style>
